import { useState, useEffect, useMemo, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, ArrowUpDown, Package, DollarSign, ArrowDownToLine, ShoppingBag, AlertTriangle, RefreshCw, Store, TrendingUp, Loader2 } from "lucide-react";
import axios from "axios";

import { API_URL as API } from "@/lib/api";

const formatBs = (v) => new Intl.NumberFormat('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) + ' Bs';

const MONETARY_KEYS = ['pmenor', 'pmayor', 'precio_unit', 'subtotal'];

const TABS_ALL = [
  { key: "inventario", label: "Inventario", icon: Package, roles: ["admin", "supervisor", "viewer"] },
  { key: "ingresos", label: "Ingresos QMS", icon: ArrowDownToLine, roles: ["admin", "supervisor"] },
  { key: "ventas", label: "Ventas MS", icon: ShoppingBag, roles: ["admin"] },
];

const INV_COLS = [
  { key: "codigo", label: "Codigo" },
  { key: "descripcion", label: "Descripcion" },
  { key: "familia", label: "Familia" },
  { key: "subfamilia", label: "SubFamilia" },
  { key: "unidad", label: "Unidad" },
  { key: "stock", label: "Stock" },
  { key: "ingreso", label: "Ingreso" },
  { key: "salida", label: "Salida" },
  { key: "pmenor", label: "PMenor" },
  { key: "pmayor", label: "PMayor" },
  { key: "lote", label: "Lote" },
];

const ING_COLS = [
  { key: "fecha", label: "Fecha" },
  { key: "codigo", label: "Codigo" },
  { key: "descripcion", label: "Descripcion" },
  { key: "familia", label: "Familia" },
  { key: "cantidad", label: "Cant." },
  { key: "pmenor", label: "PMenor" },
  { key: "pmayor", label: "PMayor" },
  { key: "tipo_entrega", label: "Tipo" },
];

const VEN_COLS = [
  { key: "fecha", label: "Fecha" },
  { key: "codigo", label: "Codigo" },
  { key: "descripcion", label: "Descripcion" },
  { key: "familia", label: "Familia" },
  { key: "cantidad", label: "Cant." },
  { key: "tipo_precio", label: "P1/P2" },
  { key: "precio_unit", label: "P.Unit" },
  { key: "subtotal", label: "SubTotal" },
];

const LocalStoreModal = ({ open, onOpenChange, localId, localName, accentColor, tipoCambio, userRole = "admin" }) => {
  const [activeTab, setActiveTab] = useState("inventario");
  const [dashboard, setDashboard] = useState(null);
  const [data, setData] = useState({ inventario: [], ingresos: [], ventas: [] });
  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(false);

  const tc = tipoCambio || 1;
  const formatUSD = (v) => '$ ' + new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(v / tc);

  const tabs = useMemo(() => TABS_ALL.filter(t => t.roles.includes(userRole)), [userRole]);
  const filterCols = useCallback((allCols) =>
    userRole === "viewer" ? allCols.filter(c => !MONETARY_KEYS.includes(c.key)) : allCols
  , [userRole]);

  const fetchData = useCallback(async () => {
    if (!open || !localId) return;
    setLoading(true);
    try {
      const [dashRes, invRes, ingRes, venRes] = await Promise.all([
        axios.get(`${API}/api/local/${localId}/dashboard`),
        axios.get(`${API}/api/local/${localId}/inventario`),
        axios.get(`${API}/api/local/${localId}/ingresos`),
        axios.get(`${API}/api/local/${localId}/ventas`),
      ]);
      setDashboard(dashRes.data);
      setData({
        inventario: invRes.data.data || [],
        ingresos: ingRes.data.data || [],
        ventas: venRes.data.data || [],
      });
    } catch (e) {
      console.error("Error fetching local data:", e);
    } finally {
      setLoading(false);
    }
  }, [open, localId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/api/local/${localId}/sync-all`);
      await fetchData();
    } catch (e) {
      console.error("Sync error:", e);
    } finally {
      setSyncing(false);
    }
  };

  const currentData = data[activeTab] || [];
  const cols = activeTab === "inventario" ? filterCols(INV_COLS) : activeTab === "ingresos" ? filterCols(ING_COLS) : filterCols(VEN_COLS);

  const filtered = useMemo(() => {
    if (!search) return currentData;
    const t = search.toLowerCase();
    return currentData.filter(r =>
      (r.codigo || '').toLowerCase().includes(t) ||
      (r.descripcion || '').toLowerCase().includes(t) ||
      (r.familia || '').toLowerCase().includes(t)
    );
  }, [currentData, search]);

  const sorted = useMemo(() => {
    if (!sortConfig.key) return filtered;
    return [...filtered].sort((a, b) => {
      const av = a[sortConfig.key], bv = b[sortConfig.key];
      if (typeof av === 'string') return sortConfig.direction === 'asc' ? (av || '').localeCompare(bv || '') : (bv || '').localeCompare(av || '');
      return sortConfig.direction === 'asc' ? (av || 0) - (bv || 0) : (bv || 0) - (av || 0);
    });
  }, [filtered, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(p => ({ key, direction: p.key === key && p.direction === 'asc' ? 'desc' : 'asc' }));
  };

  const inv = dashboard?.inventario || {};
  const ing = dashboard?.ingresos || {};
  const ven = dashboard?.ventas || {};

  const renderCell = (row, col) => {
    const v = row[col.key];
    if (col.key === 'descripcion') return <span className="block max-w-[240px] leading-snug line-clamp-2">{v}</span>;
    if (col.key === 'stock') {
      const s = v || 0;
      return <span className={`font-bold ${s === 0 ? 'text-red-600' : s < 10 ? 'text-amber-600' : 'text-emerald-700'}`}>{s}</span>;
    }
    if (['pmenor','pmayor','precio_unit','subtotal','precio1','precio2'].includes(col.key)) return formatBs(v || 0);
    if (col.key === 'fecha') return <span className="text-[#FF9900] font-medium">{v || ''}</span>;
    if (col.key === 'tipo_precio') return v ? <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px] border">{v}</Badge> : '';
    if (col.key === 'tipo_entrega') return v ? <Badge variant="outline" className="text-[10px]">{v}</Badge> : '';
    return v ?? '';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl h-[92vh] overflow-hidden flex flex-col" data-testid={`modal-local-store-${localId}`}>
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="heading-font text-lg flex items-center gap-2">
              <Store className={`h-5 w-5 ${accentColor}`} />
              {localName}
            </DialogTitle>
            {(userRole === "admin" || userRole === "supervisor") && (
            <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing} data-testid="local-sync-btn"
              className="text-xs gap-1.5">
              {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              {syncing ? 'Sincronizando...' : 'Sincronizar'}
            </Button>
            )}
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
        ) : (
          <>
            {/* KPI Summary Row */}
            <div className={`grid grid-cols-2 md:grid-cols-4 ${userRole === "admin" ? "lg:grid-cols-7" : "lg:grid-cols-4"} gap-2 flex-shrink-0`} data-testid="local-kpis">
              <Card className="p-2 border border-border">
                <p className="text-[10px] text-muted-foreground uppercase">Productos</p>
                <p className="text-sm font-bold text-foreground">{(inv.total_productos || 0).toLocaleString()}</p>
              </Card>
              <Card className="p-2 border border-border">
                <p className="text-[10px] text-muted-foreground uppercase">Unidades Stock</p>
                <p className="text-sm font-bold text-foreground">{(inv.total_unidades || 0).toLocaleString()}</p>
              </Card>
              {userRole === "admin" && (
              <Card className="p-2 border border-emerald-200 bg-emerald-50/50">
                <p className="text-[10px] text-emerald-700 uppercase">Valor Inventario</p>
                <p className="text-sm font-bold text-emerald-800">{formatBs(inv.valor_pmenor || 0)}</p>
                <p className="text-[10px] text-emerald-600">{formatUSD(inv.valor_pmenor || 0)}</p>
              </Card>
              )}
              <Card className="p-2 border border-amber-200 bg-amber-50/50">
                <p className="text-[10px] text-amber-700 uppercase flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Sin Stock / Bajo</p>
                <p className="text-sm font-bold text-amber-800">{inv.sin_stock || 0} / {inv.stock_bajo || 0}</p>
              </Card>
              {userRole === "admin" && (
              <>
              <Card className="p-2 border border-blue-200 bg-blue-50/50">
                <p className="text-[10px] text-blue-700 uppercase">Ingresos QMS</p>
                <p className="text-sm font-bold text-blue-800">{(ing.total_ingresos || 0).toLocaleString()}</p>
                <p className="text-[10px] text-blue-600">{(ing.total_unidades_ingreso || 0).toLocaleString()} uds</p>
              </Card>
              <Card className="p-2 border border-violet-200 bg-violet-50/50">
                <p className="text-[10px] text-violet-700 uppercase">Ventas MS</p>
                <p className="text-sm font-bold text-violet-800">{(ven.total_ventas || 0).toLocaleString()}</p>
                <p className="text-[10px] text-violet-600">{(ven.total_unidades_venta || 0).toLocaleString()} uds</p>
              </Card>
              <Card className="p-2 border border-[#FF9900]/30 bg-muted/50">
                <p className="text-[10px] text-[#FF9900] uppercase flex items-center gap-1"><TrendingUp className="h-3 w-3" />Facturado</p>
                <p className="text-sm font-bold text-[#FF9900]">{formatBs(ven.total_facturado || 0)}</p>
                <p className="text-[10px] text-[#FF9900]">{formatUSD(ven.total_facturado || 0)}</p>
              </Card>
              </>
              )}
            </div>

            {/* Sub-tabs */}
            <div className="flex gap-1 border-b border-border flex-shrink-0" data-testid="local-tabs">
              {tabs.map(tab => (
                <button key={tab.key} onClick={() => { setActiveTab(tab.key); setSearch(''); setSortConfig({ key: null, direction: 'asc' }); }}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-foreground text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`} data-testid={`local-tab-${tab.key}`}>
                  <tab.icon className="h-3.5 w-3.5" />
                  {tab.label}
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{(data[tab.key] || []).length}</Badge>
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative flex-shrink-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Buscar por codigo, descripcion o familia..."
                className="pl-10 text-xs h-8" data-testid="local-search" />
            </div>

            <p className="text-[11px] text-muted-foreground flex-shrink-0" data-testid="local-count">
              {filtered.length} de {currentData.length} registros
            </p>

            {/* Table */}
            <div className="flex-1 min-h-0 overflow-auto border border-border rounded-lg">
              <Table>
                <TableHeader className="sticky top-0 bg-muted/95 backdrop-blur z-10">
                  <TableRow>
                    {cols.map(col => (
                      <TableHead key={col.key} className="body-font font-semibold">
                        <Button variant="ghost" size="sm" onClick={() => handleSort(col.key)} className="text-[11px] px-1 h-7">
                          {col.label} <ArrowUpDown className="ml-1 h-2.5 w-2.5" />
                        </Button>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.length === 0 ? (
                    <TableRow><TableCell colSpan={cols.length} className="text-center py-8 text-xs text-muted-foreground">
                      {currentData.length === 0 ? 'Sin datos. Presiona "Sincronizar" para importar.' : 'Sin resultados para la busqueda'}
                    </TableCell></TableRow>
                  ) : sorted.map((row, i) => (
                    <TableRow key={i} className="hover:bg-muted" data-testid={`local-row-${i}`}>
                      {cols.map(col => (
                        <TableCell key={col.key} className="mono-font text-xs">{renderCell(row, col)}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LocalStoreModal;
